import { useState, type ReactNode } from "react";
import { Alert, Badge, Button, Code, Group, Stack, Text } from "@mantine/core";
import {
  IconAlertTriangle,
  IconBolt,
  IconBuildingStore,
  IconCalendarEvent,
  IconChartHistogram,
  IconCheck,
  IconDatabaseImport,
  IconFileText,
  IconPlugConnected,
  IconRefresh,
  IconReload,
  IconTrash,
  IconUsersGroup,
  IconWand,
  IconX,
} from "@tabler/icons-react";
import { useNavigate } from "@/routing/accountRouting";
import {
  claimActionProposal,
  dismissActionProposal,
  releaseActionProposal,
  resolveActionProposal,
} from "@/api/actionProposals";
import { ApiError } from "@/api/client";
import type { ActionProposal } from "@/types/actionProposals";
import { reportPeriodLabel } from "@/constants/reportPeriods";
import { ACTION_CANCELLED, ActionError, getActionExecutor } from "./registry";
import classes from "./ActionProposalCard.module.css";

interface ActionProposalCardProps {
  messageId: string;
  proposal: ActionProposal;
}

const RISK_COLOR: Record<ActionProposal["risk"], string> = {
  low: "teal",
  medium: "yellow",
  high: "orange",
};

// Each action type gets its own icon so the card is recognisable at a glance
// instead of a generic bolt on everything.
const ACTION_ICON: Record<string, typeof IconBolt> = {
  connect_source: IconPlugConnected,
  create_segment: IconUsersGroup,
  propose_segment: IconUsersGroup,
  rebuild_segment: IconReload,
  trigger_sync: IconRefresh,
  trigger_transform: IconWand,
  trigger_backfill: IconDatabaseImport,
  start_data_analysis: IconChartHistogram,
  update_business_profile: IconBuildingStore,
  create_context_document: IconFileText,
  update_context_document: IconFileText,
  create_context_event: IconCalendarEvent,
  update_context_event: IconCalendarEvent,
  delete_context_document: IconTrash,
  delete_context_event: IconTrash,
};

function actionIcon(actionType: string) {
  return ACTION_ICON[actionType] ?? IconBolt;
}

const GENERIC_ACTION_ERROR =
  "The action could not be completed. Please try again from the relevant page.";

// Surface the real, actionable reason a confirm failed (e.g. "This data source
// is paused. Resume it first.") instead of a generic message. apiJson already
// builds a human `.message` from the backend detail; fall back to generic for
// network errors or empty messages.
function actionErrorMessage(err: unknown): string {
  // An executor's own refusal is already written for the user — pass it
  // through rather than replacing it with the generic fallback.
  if (err instanceof ActionError && err.message) return err.message;
  if (err instanceof ApiError && err.message && err.message !== "Request failed") {
    return err.message;
  }
  return GENERIC_ACTION_ERROR;
}

// A soft-tinted rounded tile carrying the action icon — the card's signature.
function IconTile({ color, children }: { color: string; children: ReactNode }) {
  return (
    <div
      className={classes.tile}
      style={{
        background: `var(--mantine-color-${color}-light)`,
        color: `var(--mantine-color-${color}-light-color)`,
      }}
    >
      {children}
    </div>
  );
}

const PREVIEW_LABELS: Record<string, string> = {
  table_fqn: "Table",
  data_source_name: "Source",
  connector: "Connector",
  auth: "Method",
  workspace: "Workspace",
  count: "Matches",
  total: "Total",
  percentage: "Share",
  estimated_scan_bytes: "Scan estimate",
  rules: "Rules",
};

function previewLabel(key: string): string {
  if (PREVIEW_LABELS[key]) return PREVIEW_LABELS[key];
  const words = key.replaceAll("_", " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// Customer-facing wording for values that are otherwise technical.
const AUTH_KIND_LABEL: Record<string, string> = {
  oauth: "Secure sign-in",
  api_key: "API key",
  iam: "Access grant",
};

function humanBytes(n: number): string {
  if (n >= 1e9) return `≈ ${(n / 1e9).toLocaleString(undefined, { maximumFractionDigits: 1 })} GB`;
  if (n >= 1e6) return `≈ ${(n / 1e6).toLocaleString(undefined, { maximumFractionDigits: 1 })} MB`;
  if (n >= 1e3) return `≈ ${(n / 1e3).toLocaleString(undefined, { maximumFractionDigits: 1 })} KB`;
  return `${n} B`;
}

function previewValue(key: string, value: unknown): string {
  if (key === "auth" && typeof value === "string") {
    return AUTH_KIND_LABEL[value] ?? value;
  }
  if (key === "table_fqn" && typeof value === "string") {
    // Show just the table name, not the full project.dataset.table FQN —
    // the project/dataset are noise for the customer and overflow the card.
    return value.split(".").pop() || value;
  }
  if (typeof value === "number") {
    if (key === "percentage") {
      return `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
    }
    if (key === "estimated_scan_bytes") return humanBytes(value);
    return value.toLocaleString();
  }
  return String(value);
}

// ── Business-context cards (plan 2026-08-09, §8) ────────────────────────────
//
// These action types cannot use the generic preview path above, for two
// separate reasons, so they render their own body. Everything below renders
// model-authored strings as TEXT — never markup.

const PROFILE_FIELD_LABELS: Record<string, string> = {
  industry: "Industry",
  description: "What the business does",
  business_model: "Business model",
  customer_type: "Sells to",
  markets: "Markets",
  seasonality: "Seasonality",
  goals: "Goals",
  competitors: "Competitors",
};

const CUSTOMER_TYPE_LABELS: Record<string, string> = {
  b2c: "Consumers (B2C)",
  b2b: "Businesses (B2B)",
  both: "Both consumers and businesses",
};

const NOT_SET = "(not set)";

function profileValue(field: string, value: unknown): string {
  if (value === null || value === undefined || value === "") return NOT_SET;
  if (Array.isArray(value)) return value.length ? value.join(", ") : NOT_SET;
  if (field === "customer_type") return CUSTOMER_TYPE_LABELS[String(value)] ?? String(value);
  return String(value);
}

/**
 * `current → proposed`, one row per field that will actually be written.
 *
 * Driven by `params` rather than `preview` on purpose. The generic preview
 * filter drops any entry whose stringified value the title contains, and
 * `"anything".includes("")` is true — so every *unset* current value would
 * vanish, which is the common case when a customer first fills their profile.
 * Iterating `params` also guarantees the card shows every key that executes.
 * `preview.changes` is consulted only for the before-values.
 */
function ProfileDiff({ proposal }: { proposal: ActionProposal }) {
  const rawChanges = (proposal.preview as { changes?: unknown } | undefined)?.changes;
  const before = new Map<string, unknown>(
    Array.isArray(rawChanges)
      ? rawChanges
          .filter((c): c is { field: string; current: unknown } =>
            typeof c === "object" && c !== null && "field" in c)
          .map((c) => [c.field, c.current])
      : [],
  );

  const fields = Object.keys(proposal.params ?? {});
  if (fields.length === 0) return null;

  return (
    <>
      <hr className={classes.rule} />
      <Stack gap={10}>
        {fields.map((field) => {
          const currentText = profileValue(field, before.get(field));
          return (
            <Stack key={field} gap={2}>
              <Text className={classes.metaLabel}>
                {PROFILE_FIELD_LABELS[field] ?? previewLabel(field)}
              </Text>
              <Text size="xs" c="dimmed" style={{ textDecoration: currentText === NOT_SET ? undefined : "line-through" }}>
                {currentText}
              </Text>
              <Text size="sm" style={{ wordBreak: "break-word" }}>
                {profileValue(field, proposal.params[field])}
              </Text>
            </Stack>
          );
        })}
      </Stack>
      <Text size="xs" c="dimmed">
        Current values as they were when this was proposed. Confirming changes
        only the fields listed here.
      </Text>
    </>
  );
}

/**
 * The proposed document, in full.
 *
 * A context document's text can be retrieved into any later answer, so
 * confirming it unseen is not a confirm. Scrollable rather than truncated —
 * the tool caps proposals at 10,000 characters precisely so the whole body
 * can be shown here.
 */
function DocumentBody({ proposal }: { proposal: ActionProposal }) {
  const params = proposal.params ?? {};
  const title = typeof params.title === "string" ? params.title : null;
  const description =
    typeof params.description === "string" ? params.description : null;
  const content = typeof params.content === "string" ? params.content : "";

  return (
    <>
      <hr className={classes.rule} />
      <Stack gap={8}>
        {title && (
          <Group gap="sm" wrap="nowrap" align="baseline">
            <Text className={classes.metaLabel}>Title</Text>
            <Text className={classes.metaValue} style={{ flex: 1, minWidth: 0 }}>
              {title}
            </Text>
          </Group>
        )}
        {description && (
          <Stack gap={2}>
            <Text className={classes.metaLabel}>When to use it</Text>
            <Text size="sm" style={{ wordBreak: "break-word" }}>
              {description}
            </Text>
          </Stack>
        )}
        <Stack gap={4}>
          <Text className={classes.metaLabel}>Document text</Text>
          <div className={classes.documentBody}>{content}</div>
        </Stack>
      </Stack>
    </>
  );
}

/** What disappears: title, what it was for, and the opening of the text. */
function DeletionBody({ proposal }: { proposal: ActionProposal }) {
  const preview = (proposal.preview ?? {}) as Record<string, unknown>;
  const rows: [string, string][] = [];
  const push = (label: string, value: unknown) => {
    if (typeof value === "string" && value.trim()) rows.push([label, value]);
  };
  push("Title", preview.document_title ?? preview.event_title);
  push("Date", preview.date);
  push("Category", preview.category);
  push("When to use it", preview.description);

  const contentPreview =
    typeof preview.content_preview === "string" ? preview.content_preview : null;

  if (rows.length === 0 && !contentPreview) return null;

  return (
    <>
      <hr className={classes.rule} />
      <Stack gap={6}>
        {rows.map(([label, value]) => (
          <Group key={label} gap="sm" wrap="nowrap" align="baseline">
            <Text className={classes.metaLabel}>{label}</Text>
            <Text className={classes.metaValue} style={{ flex: 1, minWidth: 0 }}>
              {value}
            </Text>
          </Group>
        ))}
        {contentPreview && (
          <Stack gap={4}>
            <Text className={classes.metaLabel}>Text</Text>
            <div className={classes.documentBody}>{contentPreview}</div>
          </Stack>
        )}
      </Stack>
    </>
  );
}

/**
 * The create-report card (ADR-0495): a draft block report, so what the user
 * confirms is where it goes and what it starts from: workspace, template
 * ("Empty report" without one), period and page format, in words.
 */
function CreateReportBody({ proposal }: { proposal: ActionProposal }) {
  const preview = (proposal.preview ?? {}) as Record<string, unknown>;
  const text = (v: unknown) => (typeof v === "string" && v.trim() ? v : null);
  const format = text(preview.page_format);
  const rows: [string, string | null][] = [
    ["Workspace", text(preview.workspace)],
    ["Starts from", text(preview.template)],
    ["Period", text(preview.period) && reportPeriodLabel(preview.period as string)],
    ["Page", format && (format === "landscape" ? "Landscape" : "Portrait")],
  ];
  return (
    <>
      <hr className={classes.rule} />
      <Stack gap={6}>
        {rows
          .filter((row): row is [string, string] => Boolean(row[1]))
          .map(([label, value]) => (
            <Group key={label} gap="sm" wrap="nowrap" align="baseline">
              <Text className={classes.metaLabel}>{label}</Text>
              <Text className={classes.metaValue} style={{ flex: 1, minWidth: 0 }}>
                {value}
              </Text>
            </Group>
          ))}
      </Stack>
    </>
  );
}

/**
 * The Analyst edition's edit card (plan 2026-08-23 assistant-report-
 * authoring). The instruction IS what the writer receives — a 4000-char payload the generic
 * preview rows would clip — so it gets a full-width body. The other preview
 * fields (title, workspace, template, sections, metrics, review status and
 * the demote warning) render as rows above it.
 */
function ReportInstructionBody({ proposal }: { proposal: ActionProposal }) {
  const preview = (proposal.preview ?? {}) as Record<string, unknown>;
  const instruction =
    typeof preview.instruction === "string" ? preview.instruction : null;
  const warning = typeof preview.warning === "string" ? preview.warning : null;
  const rows = Object.entries(preview).filter(
    ([key, value]) =>
      key !== "instruction" &&
      key !== "warning" &&
      value !== null &&
      value !== undefined &&
      value !== "",
  );
  return (
    <>
      <hr className={classes.rule} />
      <Stack gap={8}>
        {rows.map(([key, value]) => (
          <Group key={key} gap="sm" wrap="nowrap" align="baseline">
            <Text className={classes.metaLabel}>{previewLabel(key)}</Text>
            <Text className={classes.metaValue} style={{ flex: 1, minWidth: 0 }}>
              {previewValue(key, value)}
            </Text>
          </Group>
        ))}
        {warning && (
          <Alert color="yellow" variant="light" p="xs">
            <Text size="sm">{warning}</Text>
          </Alert>
        )}
        {instruction && (
          <Stack gap={2}>
            <Text size="xs" c="dimmed">
              Instruction
            </Text>
            <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
              {instruction}
            </Text>
          </Stack>
        )}
      </Stack>
    </>
  );
}

/**
 * The customer model card (plan 2026-10-21-customer-data-modeling-platform,
 * P2.9). The SQL is what the draft will hold, so it is shown whole, as text in
 * a code block, not clipped into a row; problems the editor's Validate would
 * report sit above it. A config replaces the draft's whole config, so the card
 * shows both the new one and the one it replaces.
 */
function DataModelBody({ proposal }: { proposal: ActionProposal }) {
  const preview = (proposal.preview ?? {}) as Record<string, unknown>;
  const sql = typeof preview.sql === "string" ? preview.sql : null;
  const problems = Array.isArray(preview.problems)
    ? preview.problems.filter((p): p is string => typeof p === "string")
    : [];
  const config = typeof preview.config === "string" ? preview.config : null;
  const currentConfig = typeof preview.current_config === "string" ? preview.current_config : null;
  const blocks = new Set(["sql", "problems", "config", "current_config"]);
  const rows = Object.entries(preview).filter(
    ([key, value]) => !blocks.has(key) && value !== null && value !== undefined && value !== "",
  );
  return (
    <>
      <hr className={classes.rule} />
      <Stack gap={8}>
        {rows.map(([key, value]) => (
          <Group key={key} gap="sm" wrap="nowrap" align="baseline">
            <Text className={classes.metaLabel}>{previewLabel(key)}</Text>
            <Text className={classes.metaValue} style={{ flex: 1, minWidth: 0 }}>
              {previewValue(key, value)}
            </Text>
          </Group>
        ))}
        {problems.length > 0 && (
          <Alert color="yellow" variant="light" p="xs" data-testid="data-model-problems">
            <Text size="sm">Validate will report:</Text>
            {problems.map((p) => (
              <Text key={p} size="sm">
                {p}
              </Text>
            ))}
          </Alert>
        )}
        {sql && (
          <Code block style={{ maxHeight: 240, overflow: "auto", whiteSpace: "pre-wrap" }}>
            {sql}
          </Code>
        )}
        {config && (
          <Stack gap={2} data-testid="data-model-config">
            <Text size="xs" c="dimmed">
              {currentConfig ? "Settings (replace all current settings)" : "Settings"}
            </Text>
            <Code block style={{ whiteSpace: "pre-wrap" }}>{config}</Code>
            {currentConfig && (
              <>
                <Text size="xs" c="dimmed">
                  Current settings
                </Text>
                <Code block style={{ whiteSpace: "pre-wrap" }}>{currentConfig}</Code>
              </>
            )}
          </Stack>
        )}
      </Stack>
    </>
  );
}

// Partial: most action types have NO dedicated body and take the generic
// preview path — a plain Record would type every lookup as defined.
const CONTEXT_BODY: Partial<Record<string, (p: { proposal: ActionProposal }) => ReactNode>> = {
  create_report: CreateReportBody,
  iterate_report: ReportInstructionBody,
  update_business_profile: ProfileDiff,
  create_context_document: DocumentBody,
  update_context_document: DocumentBody,
  delete_context_document: DeletionBody,
  delete_context_event: DeletionBody,
  save_data_model_draft: DataModelBody,
};

/**
 * Generic confirm card for agent-proposed platform actions.
 *
 * Claim-before-execute: the confirm click first claims the proposal (CAS on
 * the persisted message — a second tab or double click gets the current state
 * back instead of firing the mutation twice), then runs the registered REST
 * executor under the user's own JWT, then records the outcome.
 */
export function ActionProposalCard({ messageId, proposal: initial }: ActionProposalCardProps) {
  const navigate = useNavigate();
  const [proposal, setProposal] = useState<ActionProposal>(initial);
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState<{ label: string; to: string } | null>(null);
  const [claimError, setClaimError] = useState<string | null>(null);

  const executor = getActionExecutor(proposal.action_type);
  const ActionIcon = actionIcon(proposal.action_type);

  const handleConfirm = async () => {
    if (busy) return;
    setBusy(true);
    setClaimError(null);
    try {
      const claim = await claimActionProposal(messageId, proposal.proposal_id);
      if (!claim.claimed) {
        // Another tab/click got there first — render its terminal state.
        setProposal(claim.proposal);
        return;
      }
      setProposal(claim.proposal);

      if (!executor) {
        const res = await resolveActionProposal(messageId, proposal.proposal_id, {
          status: "failed",
          error: "This action is not available yet.",
        });
        setProposal(res.proposal);
        return;
      }

      try {
        const result = await executor(proposal.params);
        const res = await resolveActionProposal(messageId, proposal.proposal_id, {
          status: "confirmed",
          resultRef: result.resultRef,
        });
        setProposal(res.proposal);
        if (result.link) setLink(result.link);
        if (result.navigateTo) navigate(result.navigateTo);
      } catch (err) {
        // Cancelled interactive handoff (e.g. the user closed the connect-source
        // wizard): return the card to actionable so it can be retried, instead
        // of the terminal failed state.
        if (err === ACTION_CANCELLED) {
          const res = await releaseActionProposal(messageId, proposal.proposal_id);
          setProposal(res.proposal);
          return;
        }
        const res = await resolveActionProposal(messageId, proposal.proposal_id, {
          status: "failed",
          error: actionErrorMessage(err),
        });
        setProposal(res.proposal);
      }
    } catch {
      // Claim failed (network/auth/stale card) — keep the card actionable but
      // never fail silently: an unexplained dead button reads as "broken".
      setClaimError("The action could not be started. Please try again, or ask the assistant to propose it again.");
    } finally {
      setBusy(false);
    }
  };

  const handleDismiss = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await dismissActionProposal(messageId, proposal.proposal_id);
      setProposal(res.proposal);
    } catch {
      // Keep the card actionable on failure.
    } finally {
      setBusy(false);
    }
  };

  // ── Terminal states ────────────────────────────────────────────────────
  if (proposal.status === "confirmed") {
    return (
      <div className={classes.card} style={{ padding: 16 }}>
        <Group align="flex-start" gap="sm" wrap="nowrap">
          <IconTile color="teal">
            <IconCheck size={20} />
          </IconTile>
          <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
            <Text fw={600} size="sm">
              {proposal.title}
            </Text>
            <Text size="xs" fw={600} c="teal.7">
              Done
            </Text>
            {link && (
              <Button
                variant="light"
                size="xs"
                mt={4}
                style={{ alignSelf: "flex-start" }}
                onClick={() => navigate(link.to)}
              >
                {link.label}
              </Button>
            )}
          </Stack>
        </Group>
      </div>
    );
  }

  if (proposal.status === "dismissed") {
    return (
      <div className={classes.card} style={{ padding: "12px 16px" }}>
        <Group gap="sm" wrap="nowrap">
          <IconTile color="gray">
            <IconX size={18} />
          </IconTile>
          <Text size="sm" c="dimmed">
            Dismissed — {proposal.title}
          </Text>
        </Group>
      </div>
    );
  }

  if (proposal.status === "failed") {
    return (
      <div className={classes.card} style={{ padding: 16 }}>
        <Group align="flex-start" gap="sm" wrap="nowrap">
          <IconTile color="red">
            <IconAlertTriangle size={19} />
          </IconTile>
          <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
            <Text fw={600} size="sm">
              {proposal.title}
            </Text>
            <Text size="sm" c="dimmed">
              {proposal.error || "The action could not be completed."}
            </Text>
          </Stack>
        </Group>
      </div>
    );
  }

  // ── Actionable state ───────────────────────────────────────────────────
  const confirming = proposal.status === "confirming" || busy;

  // Action types that render their own body (see CONTEXT_BODY above) opt out
  // of the generic preview path entirely rather than showing both.
  const DedicatedBody = CONTEXT_BODY[proposal.action_type];

  const previewEntries = DedicatedBody
    ? []
    : Object.entries(proposal.preview ?? {}).filter(([, value]) => {
        // An absent value is not a row — `String(null)` would render "null".
        if (value === null || value === undefined || value === "") return false;
        if (Array.isArray(value)) return value.length > 0;
        // Drop STRING scalars whose value is already in the title (e.g.
        // "Facebook Ads" under "Connect Facebook Ads") — no premium card
        // repeats itself. Numbers are data, not names, and are never subject
        // to this dedupe: `count: 0` would otherwise be dropped by any title
        // containing a year ("...Q1 2026...") since `String(0)` is "0" and
        // "2026".includes("0") is true — exactly when a segment is empty.
        if (typeof value !== "string") return true;
        return !proposal.title.toLowerCase().includes(value.toLowerCase());
      });

  return (
    <div className={classes.card} style={{ padding: 16 }}>
      <Group align="flex-start" gap="sm" wrap="nowrap">
        <IconTile color="blue">
          <ActionIcon size={20} />
        </IconTile>

        <Stack gap={10} style={{ flex: 1, minWidth: 0 }}>
          {/* Title + impact */}
          <Group gap="xs" justify="space-between" wrap="nowrap" align="flex-start">
            <Text fw={600} size="sm" lh={1.3}>
              {proposal.title}
            </Text>
            {proposal.risk !== "low" && (
              <Badge
                size="sm"
                variant="light"
                radius="sm"
                color={RISK_COLOR[proposal.risk]}
                style={{ flexShrink: 0 }}
              >
                {proposal.risk === "high" ? "High impact" : "Medium impact"}
              </Badge>
            )}
          </Group>

          {proposal.summary && (
            <Text size="sm" c="dimmed" lh={1.5}>
              {proposal.summary}
            </Text>
          )}

          {DedicatedBody && <DedicatedBody proposal={proposal} />}

          {/* Meta / preview */}
          {previewEntries.length > 0 && (
            <>
              <hr className={classes.rule} />
              <Stack gap={6}>
                {previewEntries.map(([key, value]) =>
                  Array.isArray(value) ? (
                    <Stack key={key} gap={4}>
                      <Text className={classes.metaLabel}>{previewLabel(key)}</Text>
                      <div className={classes.rulesBlock}>
                        {value.map((line, i) => (
                          <div key={i} className={classes.ruleLine}>
                            {String(line)}
                          </div>
                        ))}
                      </div>
                    </Stack>
                  ) : (
                    <Group key={key} gap="sm" wrap="nowrap" align="baseline">
                      <Text className={classes.metaLabel}>{previewLabel(key)}</Text>
                      <Text className={classes.metaValue} style={{ flex: 1, minWidth: 0 }}>
                        {previewValue(key, value)}
                      </Text>
                    </Group>
                  ),
                )}
              </Stack>
            </>
          )}

          {claimError && (
            <Alert color="red" icon={<IconAlertTriangle size={16} />} p="xs" radius="md">
              {claimError}
            </Alert>
          )}

          {/* Actions */}
          <hr className={classes.rule} />
          <Group gap="sm">
            <Button
              size="sm"
              radius="md"
              onClick={handleConfirm}
              loading={confirming}
              disabled={!executor && !confirming}
            >
              {proposal.confirm_label || "Confirm"}
            </Button>
            <Button
              size="sm"
              radius="md"
              variant="subtle"
              color="gray"
              onClick={handleDismiss}
              disabled={confirming}
            >
              Dismiss
            </Button>
          </Group>
          {!executor && (
            <Text size="xs" c="dimmed">
              This action is not available yet.
            </Text>
          )}
        </Stack>
      </Group>
    </div>
  );
}
